<template>
  <div class="wrapper" data-step="1">
    <Header :name="`Bienvenue à bord, ${user.first_name} !`" />
    <transition name="fade" mode="out-in">
      <div v-if="currentCompany?.stats" class="stats-wrapper">
        <div class="cards-container">
          <StatsCard
            icon="wallet"
            :label="`Évaluation${pluralize(
              'activated-participants'
            )} activée${pluralize('activated-participants')} ce mois-ci`"
            :stats="currentCompany.stats['activated-participants'] || null"
          />
          <StatsCard
            icon="target"
            :label="`Talent${pluralize(
              'evaluated-participants'
            )} évalué${pluralize('evaluated-participants')} ce mois-ci`"
            :stats="currentCompany.stats['evaluated-participants'] || null"
          />
          <div class="card credit-card">
            <div class="credits">
              <span
                class="credit-number"
                v-text="currentCompany.credits.available"
              />
              <span
                class="credit-text"
                v-text="
                  `Crédit${
                    currentCompany.credits.available === 1 ? '' : 's'
                  } restant sur votre compte`
                "
              />
            </div>
            <Button
              class="primary icon icon-left sm"
              icon="bi-rocket-takeoff"
              label="Créditer votre compte"
              :disabled="!hasPermission('payments', 2)"
              @click="appStore.openPaymentModal('dashboard')"
            />
          </div>
        </div>
        <div class="cards-container">
          <div class="card">
            <div class="card-header">
              <div
                class="card-title"
                v-text="'Derniers bilans de compétence disponibles'"
              />
            </div>
            <div
              v-if="
                currentCompany.stats['last-available-results']?.entries.length >
                  0 || null
              "
              class="last-results-container"
            >
              <div
                v-for="(result, index) in currentCompany.stats[
                  'last-available-results'
                ].entries"
                :key="index"
                class="result-card"
                @click="goToCandidateResults(result)"
              >
                <ProgressCircular
                  :value="result.participant.results.compatibility"
                />
                <div class="candidate-infos">
                  <p class="bold">
                    {{ result.participant.candidate.first_name }}
                    {{ result.participant.candidate.last_name }}
                  </p>
                  <p v-text="result.campaign.name" />
                </div>
              </div>
            </div>
            <Empty
              v-else
              message="Aucun <b>bilan de compétence</b> disponible pour le moment"
              :dark="isDark"
              icon="folder"
            />
          </div>
        </div>
        <div class="cards-container duo-cards">
          <div class="card">
            <div class="card-header">
              <div
                class="card-title"
                v-text="'Taux de complétion de vos évaluations en cours'"
              />
            </div>
            <div class="completion-stats-container">
              <Doughnut
                :data="doughnutData"
                :displayTotal="true"
                :cutout="75"
                totalLabel="TALENT(S)"
              />
            </div>
          </div>
          <div class="card">
            <div class="card-header">
              <div class="card-title" v-text="'Nouveaux talents à activer'" />
            </div>
            <Table
              v-if="
                currentCompany.stats['activable-participants']?.entries
                  .length || null
              "
              :data="currentCompany.stats['activable-participants'].entries"
              :fields="state.fields"
              class="table-light"
            >
              <template #[`participant.candidate.last_name`]="{ data }">
                <span
                  class="participant-name"
                  v-if="data.participant.candidate_id"
                  v-html="
                    `${data.participant.candidate.first_name} ${data.participant.candidate.last_name}`
                  "
                />
                <span
                  class="participant-name"
                  v-else
                  v-html="
                    data.participant.pending_first_name &&
                    data.participant.pending_last_name
                      ? `${data.participant.pending_first_name} ${data.participant.pending_last_name}`
                      : data.participant.pending_email
                  "
                />
              </template>
              <template #actions="actionsProps">
                <Button
                  label="Activer l'évaluation"
                  class="primary icon icon-left sm"
                  icon="bi-play-btn"
                  @click="
                    () => {
                      state.showActionModal = true;
                      state.participant = actionsProps.row;
                    }
                  "
                />
              </template>
            </Table>
            <Empty
              v-else
              message="Vous n’avez <b>aucun talent</b> à activer pour le moment"
              :dark="isDark"
              icon="box"
            />
          </div>
        </div>
      </div>
    </transition>
    <ActionModal
      modal="activate"
      :data="[state.participant]"
      :isOpen="state.showActionModal"
      @close="closeActionModal"
      @confirm="actionModalConfirm"
    />
  </div>
</template>

<script setup>
import { reactive, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/stores/app.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useCompanyStore } from "@/stores/company.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useUserStore } from "@/stores/user.js";
import { useDark } from "@vueuse/core";
import {
  getModuleRank,
  computeModuleScore,
} from "@/composables/computeResults.js";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import Header from "@/components/Header.vue";
import Doughnut from "@/components/charts/Doughnut.vue";
import Empty from "@/components/table/Empty.vue";

const isDark = useDark();
const route = useRoute();
const router = useRouter();

// Init stores
const { user, hasPermission } = useUserStore();
const appStore = useAppStore();
const companyStore = useCompanyStore();
const campaignStore = useCampaignStore();
const participantStore = useParticipantStore();

const state = reactive({
  showActionModal: false,
  participant: null,
  activateModalIsLoading: false,
  statsToFetch: [
    "activated-participants",
    "evaluated-participants",
    "last-available-results",
    "participants-states",
    "activable-participants",
  ],
  fields: [
    {
      id: "participant.candidate.last_name",
      fallback: "participant.pending_last_name",
      label: "Talent",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "campaign.name",
      label: "Campagne",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ],
});

const currentCompany = computed(() => {
  return companyStore.currentCompany;
});

const doughnutData = computed(() => {
  return {
    labels: [
      "Évaluations complétées",
      "Évaluations en cours",
      "Évaluations non commencées",
    ],
    datasets: [
      {
        backgroundColor: [
          "#F2CB36",
          "#D9901A",
          isDark.value ? "#FFFFFF" : "#1D1D1D",
        ],
        data: currentCompany.value.stats["participants-states"]
          ? Object.values(currentCompany.value.stats["participants-states"])
          : [0],
      },
    ],
  };
});

function pluralize(stat) {
  return currentCompany.value.stats[stat]?.current_month === 1 ? "" : "s";
}

function getCompatibility(entry) {
  entry.participant.results = Object.entries(entry.participant.results).reduce(
    (acc, [skill, note]) => {
      acc[skill] = {
        rank: getModuleRank(
          note,
          entry.campaign.version,
          skill,
          entry.calibration
        ).rank,
        note,
        score: Math.min(
          computeModuleScore(
            note,
            entry.campaign.version,
            skill,
            entry.calibration
          ),
          100
        ),
      };

      return acc;
    },
    {}
  );

  entry.participant.results.compatibility =
    Object.entries(entry.participant.results).reduce(
      (acc, [, result]) => acc + result.score,
      0
    ) / Object.values(entry.participant.results).length;
}

function goToCandidateResults(entry) {
  router.push({
    name: "campaign",
    params: {
      spaceId: entry.campaign.workspace_id,
      campaignId: entry.campaign.campaign_id,
    },
    query: {
      tab: "Finished",
    },
  });

  participantStore.currentParticipant = entry.participant;
  appStore.candidatePanelIsOpen = true;
}

function closeActionModal() {
  state.showActionModal = false;
  state.participant = null;
}

async function actionModalConfirm() {
  const index = currentCompany.value.stats[
    "activable-participants"
  ].entries.findIndex(
    (obj) =>
      obj.participant.participant_id ===
      state.participant.participant.participant_id
  );

  const companyIndex = companyStore.getCompanyIndex(route.params.companyId);

  companyStore.companies[companyIndex].stats[
    "activable-participants"
  ].entries.splice(index, 1);

  closeActionModal();
}

async function startHomeOnboarding() {
  if (!appStore.isOnboarded && window.innerWidth > 991) {
    const dashboardModule = await import("@/onboarding/dashboard.js");

    appStore.startChapter(dashboardModule.default);
  } else if (!appStore.isOnboarded && window.innerWidth < 991) {
    const mobileModule = await import("@/onboarding/mobile.js");

    appStore.startChapter(mobileModule.default);
  }
  appStore.onboardingPage = "home";
}

onMounted(() => {
  if (!appStore.onboardingIsLive) {
    startHomeOnboarding();
  }

  if (!currentCompany.value.stats) {
    state.statsToFetch.forEach(async (name) => {
      const stat = await companyStore.getCompanyStats(
        route.params.companyId,
        name
      );

      const companyIndex = companyStore.getCompanyIndex(route.params.companyId);

      companyStore.companies[companyIndex].stats = {
        ...currentCompany.value.stats,
        ...stat,
      };

      if (name === "last-available-results") {
        stat[name].entries.forEach((entry) => {
          getCompatibility(entry);
          const calibrationIndex =
            campaignStore.campaignsCalibrations.findIndex(
              (calibrationObj) =>
                calibrationObj.calibration_id ===
                entry.calibration.calibration_id
            );

          if (calibrationIndex === -1) {
            campaignStore.campaignsCalibrations.push(entry.calibration);
          }
        });
      }
    });
  }
});
</script>
